import { useQuery } from "@apollo/client";
import cuid from "cuid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Container from "shared/components/Container";
import Error from "shared/components/Error";
import List from "shared/components/List";
import Loading from "shared/components/Loading";
import {
  AddSquare,
  ArrowRight,
  SquareUp,
} from "shared/components/Svg/Icons/Outline";
import ArrangeSquare from "shared/components/Svg/Icons/Outline/ArrangeSquare";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { PRODUCT_TYPES, SCREENS } from "utils/constants";
import Addresses from "./Addresses";

const Header = ({ title, actions = [] }) => {
  return (
    <div className="page__header">
      <div className="page__header--left">
        <div className="page__header--content">
          <div className="page__header--title-row">
            <div className="page__header--title">{title}</div>
          </div>
        </div>
      </div>
      <div className="page__header--actions">
        {actions.map((action) => {
          return (
            <Button
              size="small"
              key={action.key || cuid()}
              mode={action.mode}
              onClick={action.onClick}
              className={action.className}
              icon={action.icon}
            >
              {action.text}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

function Accounts() {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.XO.ACCOUNT);
  const LIST = t("LIST", { returnObjects: true });

  const { data, loading, error } = useQuery(graphql.queries.ASSETS, {
    variables: {
      where: {
        collateral: {
          escrow: { product: { type: PRODUCT_TYPES.XOB_LIQUIDITY.value } },
        },
      },
    },
  });

  if (loading) return <Loading />;
  if (error) return <Error />;

  const {
    assets: { data: assets },
  } = data;

  return (
    <Container>
      <Header
        title={LIST.HEADER.TITLE}
        actions={[
          {
            text: LIST.HEADER.ACTIONS.ADD_AMOUNT,
            icon: <AddSquare height={16} width={16} />,
            onClick: () => {
              navigate(`${SCREENS.PRIVATE.XO.ACCOUNTS.REPLENISH.path}`);
            },
            mode: "primary",
          },
          {
            text: LIST.HEADER.ACTIONS.CONVERT,
            icon: <ArrangeSquare height={16} width={16} />,
            onClick: () => {
              navigate(`${SCREENS.PRIVATE.XO.ACCOUNTS.CONVERT.path}`);
            },
            mode: "white",
          },
          {
            text: LIST.HEADER.ACTIONS.SEND,
            icon: <SquareUp height={16} width={16} />,
            onClick: () => navigate(`${SCREENS.PRIVATE.XO.ACCOUNTS.SEND.path}`),
            mode: "white",
          },
        ]}
      />
      <Addresses />
      <div className="pt-32" />
      <Card
        title={LIST.BODY.TRANSACTIONS.TITLE}
        actions={
          <Button
            mode="link"
            size="small"
            icon={<ArrowRight height={16} width={16} />}
            iconPosition="right"
          >
            {LIST.BODY.TRANSACTIONS.ACTIONS.VIEW_ALL}
          </Button>
        }
      >
        <List
          type="grouped"
          data={assets}
          render={([date, items]) => (
            <div key={date} className="transaction__group">
              <div className="transaction__list--date">
                <span>{date}</span>
              </div>
              {items.map((asset) => (
                <Card
                  key={asset.id}
                  type="custom"
                  subType="transaction"
                  asset={asset}
                />
              ))}
            </div>
          )}
        />
      </Card>
    </Container>
  );
}

export default Accounts;
